import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { Button, Modal, Grid, Checkbox } from 'semantic-ui-react';

import _startCase from 'lodash/startCase';

import ClassNameField from 'components/modules/fields/components/ClassNameField';

import {
  TEXT_ALIGNMENT_OPTIONS,
  VERTICAL_ALIGNMENT_OPTIONS,
  WIDTH_OPTIONS,
  ELEMENT_TYPE_DISPLAY_NAME,
  CSS_ENABLED_RULES,
} from './EditGridElementModal.constants';
import {
  ensureFullWidthClassName,
  isFullWidthClassName,
  removeFullWidthClassName,
} from './EditGridElementModal.utils';
import { RadioSelect, WidthInput } from './components';
import CloseableModal from '../../../../common/CloseableModal';
import { useLatestRefValue } from '../../../utils/hooks';
import { COLUMN_ELEMENT_TYPE, ROW_ELEMENT_TYPE } from '../../constants/types';
import { ColumnPropType } from '../../types/column.types';
import { RowPropType } from '../../types/row.types';
import StyleConfigurationAccordionTable from '../StyleConfigurationAccordionTable';

import './EditGridElementModal.scss';

const propTypes = {
  element: PropTypes.oneOfType([ColumnPropType, RowPropType]).isRequired,
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  editWidthSettings: PropTypes.shape({
    disabled: PropTypes.bool,
  }),
};

const defaultProps = {
  editWidthSettings: {
    disabled: false,
    maxColumnWidth: null,
  },
};

export default function EditGridElementModal({
  element,
  open,
  onCancel,
  onSave,
  editWidthSettings,
}) {
  const [loading, setLoading] = useState(false);
  const [internalElement, setInternalElement] = useState(element);
  const latestElementRef = useLatestRefValue(internalElement);

  function createChangeHandler(key) {
    return (value) => {
      const currentElement = latestElementRef.current;
      setInternalElement({ ...currentElement, [key]: value });
    };
  }

  function onStyleChange(e, { value: newStyle }) {
    const currentElement = latestElementRef.current;

    setInternalElement({ ...currentElement, style: { ...newStyle } });
  }

  function onReverseMobileOrderChange(e, { checked }) {
    const currentElement = latestElementRef.current;

    setInternalElement({ ...currentElement, reversed: checked ? 'mobile' : '' });
  }

  function onClassNameChange(e, { value: newClassName }) {
    const currentElement = latestElementRef.current;
    const { className: currentClassName } = currentElement;

    const finalNewClassName = isFullWidthClassName(currentClassName)
      ? ensureFullWidthClassName(newClassName)
      : newClassName;

    setInternalElement({ ...currentElement, className: finalNewClassName || '' });
  }

  function onFullWidthChange(e, { checked }) {
    const currentElement = latestElementRef.current;
    const { className: currentClassName } = currentElement;

    const newClassName = checked
      ? ensureFullWidthClassName(currentClassName)
      : removeFullWidthClassName(currentClassName);

    setInternalElement({ ...currentElement, className: newClassName });
  }

  async function onSaveInternal() {
    setLoading(true);
    await onSave(internalElement);
    setLoading(false);
  }

  const {
    width,
    textAlign,
    verticalAlign,
    columnSizingMode,
    style = {},
    className = '',
    type,
    reversed,
  } = internalElement || {};

  const { disabled: editWidthSettingsDisabled, maxColumnWidth } = editWidthSettings;

  const isFullWidth = isFullWidthClassName(className);

  const isColumn = type === COLUMN_ELEMENT_TYPE;
  const isRow = type === ROW_ELEMENT_TYPE;

  return (
    <CloseableModal
      open={open}
      onClose={onCancel}
      className="edit-grid-element-modal"
      size="large"
      header={`Edit ${ELEMENT_TYPE_DISPLAY_NAME[type]}`}
    >
      <Modal.Content>
        <Grid className="settings">
          <Grid.Row columns={3} className="main-settings">
            <Grid.Column width={6}>
              {isRow && (
                <>
                  <div>Column Widths</div>
                  <RadioSelect
                    value={columnSizingMode}
                    onSelect={createChangeHandler('columnSizingMode')}
                    options={WIDTH_OPTIONS}
                  />
                </>
              )}
              {isColumn && (
                <>
                  <div>Width</div>
                  <WidthInput
                    width={width}
                    onChange={createChangeHandler('width')}
                    disabled={editWidthSettingsDisabled}
                    maxAllowedWidth={maxColumnWidth}
                  />
                </>
              )}

              {isRow && (
                <>
                  <div className="form-title">Reverse Mobile Order</div>
                  <Checkbox
                    toggle
                    checked={reversed === 'mobile'}
                    onChange={onReverseMobileOrderChange}
                  />
                </>
              )}

              {isRow && (
                <>
                  <div className="form-title">Make Full Width</div>
                  <Checkbox toggle checked={isFullWidth} onChange={onFullWidthChange} />
                </>
              )}

              <>
                <div className="form-title">Class Name</div>
                <ClassNameField
                  label={null}
                  value={removeFullWidthClassName(className)}
                  onChange={onClassNameChange}
                />
              </>
            </Grid.Column>
            <Grid.Column width={5}>
              <>
                <div>Horizontal Alignment</div>
                <RadioSelect
                  value={textAlign}
                  onSelect={createChangeHandler('textAlign')}
                  options={TEXT_ALIGNMENT_OPTIONS}
                  formatLabel={_startCase}
                />
              </>
            </Grid.Column>
            <Grid.Column width={5}>
              <>
                <div>Vertical Alignment</div>
                <RadioSelect
                  value={verticalAlign}
                  onSelect={createChangeHandler('verticalAlign')}
                  options={VERTICAL_ALIGNMENT_OPTIONS}
                  formatLabel={_startCase}
                />
              </>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1} className="style-settings">
            <Grid.Column>
              <StyleConfigurationAccordionTable
                cssRules={CSS_ENABLED_RULES}
                style={style}
                onChange={onStyleChange}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onCancel} basic className="cancel">
          Cancel
        </Button>
        <Button onClick={onSaveInternal} basic className="save" loading={loading}>
          Save
        </Button>
      </Modal.Actions>
    </CloseableModal>
  );
}

EditGridElementModal.propTypes = propTypes;
EditGridElementModal.defaultProps = defaultProps;
